import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslations, TranslationsFunc } from '@lang';
import { filterNotAvailableEntities } from '@features/EntitiesListing/utils';

import { useGetAllSpaPackages } from '@features/spa/hooks/useGetAllSpaPackages';
import {
  useGetAvailableSpaPackages,
  getAvailableSpaPackagesKey,
} from '@features/spa/hooks/useGetAvailableSpaPackages';
import { useGetSpaDaysPage } from '@features/spa/hooks/useGetSpaDaysPage';

import { SpaDayListItem } from 'voyager';
import { SpaPackageListingType } from 'shared-types/SpaPackage';

type UseSpaDaysListParams = {
  propertyId: number;
  language: string;
  categoryFilterId: number | null;
  date: string;
};

const mapToListItem = (
  sd: SpaPackageListingType,
  t: TranslationsFunc,
  fallbackImageUrl?: string
): SpaDayListItem => ({
  id: String(sd.id),
  name: sd.name,
  imageUrl: sd.image?.url || fallbackImageUrl,
  price:
    sd.price?.amount != null
      ? { amount: sd.price.amount, currencyCode: sd.price.currencyCode }
      : undefined,
  discount: sd.discount?.discountedPrice
    ? {
        discountedPrice: {
          amount: sd.discount.discountedPrice.amount,
          currencyCode: sd.discount.discountedPrice.currencyCode,
        },
      }
    : undefined,
  duration: sd.durationText ? t(sd.durationText) : undefined,
  durationValue: sd.durationText ?? undefined,
  numberOfGuests: sd.numberOfGuests,
  unavailable: sd.unAvailable,
});

export function useSpaDaysList({
  propertyId,
  language,
  categoryFilterId,
  date,
}: UseSpaDaysListParams) {
  const { t } = useTranslations();
  const queryClient = useQueryClient();
  const prevParamsRef = useRef({ date, categoryFilterId });

  useEffect(() => {
    const prev = prevParamsRef.current;
    if (prev.date !== date || prev.categoryFilterId !== categoryFilterId) {
      queryClient.removeQueries({
        queryKey: getAvailableSpaPackagesKey(
          propertyId,
          { category: prev.categoryFilterId, date: prev.date },
          language
        ),
      });
      prevParamsRef.current = { date, categoryFilterId };
    }
  }, [date, categoryFilterId, propertyId, language, queryClient]);

  const { data: spaDaysAll = [] } = useGetAllSpaPackages(propertyId, language);

  const { data: spaDays = [], isFetching } = useGetAvailableSpaPackages(
    propertyId,
    { category: categoryFilterId, date },
    language
  );

  const { data: spaDaysPage } = useGetSpaDaysPage(propertyId, language);
  const fallbackImageUrl = spaDaysPage?.heroBackgroundImage?.url;

  const sortedAvailable = (spaDays || []).slice().sort((a, b) => a.displayOrder - b.displayOrder);
  const notAvailableEntities = filterNotAvailableEntities(spaDaysAll, sortedAvailable);
  const allEntities = [...sortedAvailable, ...notAvailableEntities];
  const filteredEntities = categoryFilterId
    ? allEntities.filter(({ categories }) =>
        categories.find((category) => String(category.id) === String(categoryFilterId))
      )
    : allEntities;

  const listItems: SpaDayListItem[] = filteredEntities.map((sd) =>
    mapToListItem(sd, t, fallbackImageUrl)
  );

  return {
    listItems,
    isFetching,
  };
}
